/**
 * The poll compose sheet's pure form rules (R5) and the poll bubble's pure
 * vote-tap rule (R7), split out from both components so the branching is
 * unit-testable without mounting a `Dialog` or stubbing a network call.
 */

export type typePollDraft = {
  question: string;
  allowMultiple: boolean;
  optionLabels: string[];
};

export const POLL_MIN_OPTIONS = 2;

/**
 * `Post poll` stays disabled until a non-empty question and at least two
 * non-empty options exist (R5). Blank options are allowed to exist in the
 * form (a fan can leave a trailing blank field while typing); they just
 * don't count toward the two required.
 */
export function isPollDraftValid(draft: typePollDraft): boolean {
  if (draft.question.trim().length === 0) return false;
  const nonEmptyOptions = draft.optionLabels.filter(
    (label) => label.trim().length > 0,
  );
  return nonEmptyOptions.length >= POLL_MIN_OPTIONS;
}

/** Appends one blank option field. */
export function addPollOption(optionLabels: string[]): string[] {
  return [...optionLabels, ""];
}

/**
 * Removes the option at `index`. A poll compose sheet always keeps at least
 * two option fields on screen (R5's "two or more"), so removing below that
 * floor is a no-op rather than a field the fan can delete down to zero.
 */
export function removePollOption(
  optionLabels: string[],
  index: number,
): string[] {
  if (optionLabels.length <= POLL_MIN_OPTIONS) return optionLabels;
  return optionLabels.filter((_, i) => i !== index);
}

export function updatePollOption(
  optionLabels: string[],
  index: number,
  value: string,
): string[] {
  return optionLabels.map((label, i) => (i === index ? value : label));
}

/** Trims the question and drops blank options before the draft is sent. */
export function buildSendablePollDraft(draft: typePollDraft): typePollDraft {
  return {
    question: draft.question.trim(),
    allowMultiple: draft.allowMultiple,
    optionLabels: draft.optionLabels
      .map((label) => label.trim())
      .filter((label) => label.length > 0),
  };
}

/**
 * The poll bubble's vote-tap rule (R7). A single-response poll replaces
 * whatever was selected; a multi-response poll toggles the tapped option in
 * or out of the existing selection.
 */
export function resolvePollOptionTap(input: {
  allowMultiple: boolean;
  myVoteOptionIds: readonly string[];
  tappedOptionId: string;
}): string[] {
  if (!input.allowMultiple) {
    return [input.tappedOptionId];
  }
  const isAlreadySelected = input.myVoteOptionIds.includes(
    input.tappedOptionId,
  );
  return isAlreadySelected
    ? input.myVoteOptionIds.filter((id) => id !== input.tappedOptionId)
    : [...input.myVoteOptionIds, input.tappedOptionId];
}
